import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { AdminDomainList } from './components/admin-domain-list';
import { AuthPanel } from './components/auth-panel';
import {
  DomainClaimForm,
  type IClaimDomainInput
} from './components/domain-claim-form';
import { DomainList } from './components/domain-list';
import './index.css';
import {
  ApiClientError,
  claimDomain,
  deleteDomain,
  getAdminDomains,
  getCurrentUser,
  getDomains,
  getPublicConfig,
  updateDomainSettings
} from './lib/api';
import { pocketBaseClient } from './lib/pocketbase';
import {
  authCredentialsSchema,
  authenticatedUserSchema,
  createClaimSchema,
  targetIpSchema,
  type IFlashMessage,
  type TAdminDomain,
  type TAuthCredentials,
  type TDomainConfig,
  type TPublicDomain,
  type TUserRecord
} from './shared';

const FLASH_MESSAGE_TIMEOUT_MS = 6_000;

const getApiFieldErrorMessage = (
  fields?: Record<string, string[]>
): string | undefined => {
  if (!fields) return undefined;

  for (const messages of Object.values(fields)) {
    const firstMessage = messages[0]?.trim();
    if (!firstMessage) continue;
    return firstMessage;
  }

  return undefined;
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof ApiClientError) {
    return getApiFieldErrorMessage(error.fields) ?? error.message;
  }
  if (error instanceof Error && error.message.trim().length > 0)
    return error.message;
  return fallback;
};

const AppComponent = () => {
  const [config, setConfig] = useState<TDomainConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);

  const [currentUser, setCurrentUser] = useState<TUserRecord | null>(null);
  const [domains, setDomains] = useState<TPublicDomain[]>([]);
  const [adminDomains, setAdminDomains] = useState<TAdminDomain[]>([]);
  const [isDomainsLoading, setIsDomainsLoading] = useState(false);
  const [isClaimingDomain, setIsClaimingDomain] = useState(false);
  const [isAuthActionLoading, setIsAuthActionLoading] = useState(false);
  const [pendingUpdateIds, setPendingUpdateIds] = useState<Set<string>>(
    new Set()
  );
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(
    new Set()
  );
  const [flashMessage, setFlashMessage] = useState<IFlashMessage | null>(null);
  const [isAdminDenied, setIsAdminDenied] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const isAdminRoute = window.location.pathname === '/admin';

  useEffect(() => {
    if (!flashMessage) return;

    const timeoutId = setTimeout(
      () => setFlashMessage(null),
      FLASH_MESSAGE_TIMEOUT_MS
    );

    return () => clearTimeout(timeoutId);
  }, [flashMessage]);

  const loadConfig = useCallback(async () => {
    setIsConfigLoading(true);

    try {
      const nextConfig = await getPublicConfig();
      setConfig(nextConfig);
      setConfigError(null);
    } catch (error) {
      setConfigError(
        getErrorMessage(error, 'Unable to load app configuration.')
      );
    } finally {
      setIsConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    const unsubscribe = pocketBaseClient.authStore.onChange(
      (_token, record) => {
        const parsedUser = authenticatedUserSchema.safeParse(record ?? {});
        setCurrentUser(parsedUser.success ? parsedUser.data : null);
        setIsAuthReady(true);
      },
      true
    );

    return unsubscribe;
  }, []);

  // Admin status is whatever the server says, never a flag on the local record.
  useEffect(() => {
    const authToken = pocketBaseClient.authStore.token;
    if (!currentUser || !authToken) {
      setIsAdminUser(false);
      return;
    }

    let isStale = false;
    void getCurrentUser(authToken)
      .then((user) => {
        if (!isStale) setIsAdminUser(user.isAdmin);
      })
      .catch(() => {
        if (!isStale) setIsAdminUser(false);
      });

    return () => {
      isStale = true;
    };
  }, [currentUser]);

  const loadDomains = useCallback(async () => {
    if (!currentUser) {
      setDomains([]);
      setAdminDomains([]);
      return;
    }

    const authToken = pocketBaseClient.authStore.token;
    if (!authToken) {
      setDomains([]);
      setAdminDomains([]);
      return;
    }

    setIsDomainsLoading(true);

    try {
      if (isAdminRoute) {
        // Admin access is decided by the server, not by a flag on the user
        // record the user can edit. A 403 here is the authoritative answer.
        const nextAdminDomains = await getAdminDomains(authToken);
        setAdminDomains(nextAdminDomains);
        setDomains([]);
        setIsAdminDenied(false);
      } else {
        const nextDomains = await getDomains(authToken);
        setDomains(nextDomains);
        setAdminDomains([]);
      }
    } catch (error) {
      if (error instanceof ApiClientError && error.code === 'FORBIDDEN') {
        setIsAdminDenied(true);
        setAdminDomains([]);
        return;
      }

      setFlashMessage({
        type: 'error',
        text: getErrorMessage(error, 'Unable to load domains.')
      });
    } finally {
      setIsDomainsLoading(false);
    }
  }, [currentUser, isAdminRoute]);

  useEffect(() => {
    void loadDomains();
  }, [loadDomains]);

  const onLogin = useCallback(async (credentials: TAuthCredentials) => {
    setIsAuthActionLoading(true);

    try {
      const parsedCredentials = authCredentialsSchema.parse(credentials);
      await pocketBaseClient
        .collection('users')
        .authWithPassword(parsedCredentials.email, parsedCredentials.password);
      setFlashMessage({ type: 'success', text: 'Logged in successfully.' });
    } catch (error) {
      setFlashMessage({
        type: 'error',
        text: getErrorMessage(error, 'Unable to log in with those credentials.')
      });
    } finally {
      setIsAuthActionLoading(false);
    }
  }, []);

  const onRegister = useCallback(async (credentials: TAuthCredentials) => {
    setIsAuthActionLoading(true);

    try {
      const parsedCredentials = authCredentialsSchema.parse(credentials);
      await pocketBaseClient.collection('users').create({
        email: parsedCredentials.email,
        password: parsedCredentials.password,
        passwordConfirm: parsedCredentials.password
      });

      await pocketBaseClient
        .collection('users')
        .authWithPassword(parsedCredentials.email, parsedCredentials.password);
      setFlashMessage({
        type: 'success',
        text: 'Account created and logged in.'
      });
    } catch (error) {
      setFlashMessage({
        type: 'error',
        text: getErrorMessage(error, 'Unable to create account.')
      });
    } finally {
      setIsAuthActionLoading(false);
    }
  }, []);

  const onGoogleLogin = useCallback(async () => {
    setIsAuthActionLoading(true);

    try {
      await pocketBaseClient.collection('users').authWithOAuth2({
        provider: 'google'
      });
      setFlashMessage({ type: 'success', text: 'Google login completed.' });
    } catch (error) {
      setFlashMessage({
        type: 'error',
        text: getErrorMessage(error, 'Unable to continue with Google.')
      });
    } finally {
      setIsAuthActionLoading(false);
    }
  }, []);

  const onLogout = useCallback(() => {
    pocketBaseClient.authStore.clear();
    setDomains([]);
    setAdminDomains([]);
    setFlashMessage({ type: 'success', text: 'Logged out.' });
  }, []);

  const onClaimDomain = useCallback(
    async ({
      domain: domainInput,
      targetIp,
      proxied
    }: IClaimDomainInput): Promise<boolean> => {
      if (!config) return false;

      const authToken = pocketBaseClient.authStore.token;
      if (!authToken) {
        setFlashMessage({
          type: 'error',
          text: 'You need to be logged in first.'
        });
        return false;
      }

      if (domains.length >= config.maxPerUser) {
        setFlashMessage({
          type: 'error',
          text: `You can only claim up to ${config.maxPerUser} domains.`
        });
        return false;
      }

      const parsedClaim = createClaimSchema(config.baseDomain).safeParse({
        domain: domainInput
      });
      const parsedTargetIp = targetIpSchema.safeParse(targetIp);

      if (!parsedClaim.success || !parsedTargetIp.success) {
        setFlashMessage({
          type: 'error',
          text:
            parsedClaim.error?.issues[0]?.message ??
            parsedTargetIp.error?.issues[0]?.message ??
            'Domain claim payload is invalid.'
        });
        return false;
      }

      try {
        setIsClaimingDomain(true);
        const createdDomain = await claimDomain(
          authToken,
          parsedClaim.data.domain.subdomain,
          parsedTargetIp.data,
          proxied
        );
        setDomains((previousDomains) => [createdDomain, ...previousDomains]);
        setFlashMessage({
          type: 'success',
          text: `Domain ${createdDomain.fqdn} claimed.`
        });
        return true;
      } catch (error) {
        setFlashMessage({
          type: 'error',
          text: getErrorMessage(error, 'Unable to claim domain.')
        });
        return false;
      } finally {
        setIsClaimingDomain(false);
      }
    },
    [config, domains.length]
  );

  const onUpdateDomainSettings = useCallback(
    async (
      domainId: string,
      targetIp: string,
      proxied: boolean
    ): Promise<void> => {
      const authToken = pocketBaseClient.authStore.token;
      if (!authToken) {
        setFlashMessage({
          type: 'error',
          text: 'You need to be logged in first.'
        });
        return;
      }

      const parsedTargetIp = targetIpSchema.safeParse(targetIp);
      if (!parsedTargetIp.success) {
        setFlashMessage({
          type: 'error',
          text:
            parsedTargetIp.error.issues[0]?.message ??
            'Target IP must be a valid IPv4 address.'
        });
        return;
      }

      setPendingUpdateIds((previousIds) => {
        const nextIds = new Set(previousIds);
        nextIds.add(domainId);
        return nextIds;
      });

      try {
        const updatedDomain = await updateDomainSettings(
          authToken,
          domainId,
          parsedTargetIp.data,
          proxied
        );
        setDomains((previousDomains) => {
          return previousDomains.map((domain) =>
            domain.id === domainId ? updatedDomain : domain
          );
        });
        setFlashMessage({
          type: 'success',
          text: `${updatedDomain.fqdn} now points to ${updatedDomain.targetIp} (${updatedDomain.proxied ? 'proxy on' : 'proxy off'}).`
        });
      } catch (error) {
        setFlashMessage({
          type: 'error',
          text: getErrorMessage(error, 'Unable to update domain settings.')
        });
      } finally {
        setPendingUpdateIds((previousIds) => {
          const nextIds = new Set(previousIds);
          nextIds.delete(domainId);
          return nextIds;
        });
      }
    },
    []
  );

  const onDeleteDomain = useCallback(
    async (domainId: string): Promise<void> => {
      const authToken = pocketBaseClient.authStore.token;
      if (!authToken) {
        setFlashMessage({
          type: 'error',
          text: 'You need to be logged in first.'
        });
        return;
      }

      setPendingDeleteIds((previousIds) => {
        const nextIds = new Set(previousIds);
        nextIds.add(domainId);
        return nextIds;
      });

      try {
        const deletedDomain = await deleteDomain(authToken, domainId);
        setDomains((previousDomains) =>
          previousDomains.filter((domain) => domain.id !== domainId)
        );
        setFlashMessage({
          type: 'success',
          text: `${deletedDomain.fqdn} removed successfully.`
        });
      } catch (error) {
        setFlashMessage({
          type: 'error',
          text: getErrorMessage(error, 'Unable to delete domain.')
        });
      } finally {
        setPendingDeleteIds((previousIds) => {
          const nextIds = new Set(previousIds);
          nextIds.delete(domainId);
          return nextIds;
        });
      }
    },
    []
  );

  const domainsUsageLabel = useMemo(() => {
    if (!config) return '';
    return `${domains.length} / ${config.maxPerUser} domains used`;
  }, [config, domains.length]);

  const remainingClaims = useMemo(() => {
    if (!config) return 0;
    return Math.max(0, config.maxPerUser - domains.length);
  }, [config, domains.length]);

  if (isConfigLoading || !isAuthReady) {
    return (
      <main className="app-shell">
        <section className="surface-panel mx-auto w-full max-w-md">
          <h1 className="text-xl font-semibold text-foreground">
            Sharkord Domains
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
        </section>
      </main>
    );
  }

  if (!config || configError) {
    return (
      <main className="app-shell">
        <section className="surface-panel mx-auto w-full max-w-lg">
          <h1 className="text-xl font-semibold text-foreground">
            Sharkord Domains
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {configError ?? 'Configuration is missing.'}
          </p>
          <button
            className="secondary-button mt-4"
            onClick={() => void loadConfig()}
            type="button"
          >
            Retry
          </button>
        </section>
      </main>
    );
  }

  if (!currentUser) {
    return (
      <main className="auth-shell">
        {flashMessage ? (
          <div
            className={
              flashMessage.type === 'error'
                ? 'feedback feedback-error w-full max-w-md'
                : 'feedback feedback-success w-full max-w-md'
            }
          >
            {flashMessage.text}
          </div>
        ) : null}

        <AuthPanel
          isBusy={isAuthActionLoading}
          onGoogleLogin={onGoogleLogin}
          onLogin={onLogin}
          onRegister={onRegister}
        />
      </main>
    );
  }

  if (isAdminRoute && isAdminDenied) {
    return (
      <main className="app-shell">
        <header className="surface-panel flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">
              Sharkord Domains
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Admin panel for{' '}
              <span className="font-mono">*.{config.baseDomain}</span> domains.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              className="secondary-button"
              onClick={onLogout}
              type="button"
            >
              Log out
            </button>
          </div>
        </header>

        <section className="surface-panel">
          <h2 className="text-lg font-semibold text-foreground">
            Access denied
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Your account is not allowed to access this page.
          </p>
        </section>
      </main>
    );
  }

  if (isAdminRoute) {
    return (
      <main className="app-shell">
        <header className="surface-panel flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-foreground">
              Sharkord Domains
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Admin panel for{' '}
              <span className="font-mono">*.{config.baseDomain}</span> domains.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="rounded-full border border-border bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
              Admin
            </div>
            <a className="secondary-button" href="/">
              My domains
            </a>
            <button
              className="secondary-button"
              onClick={onLogout}
              type="button"
            >
              Log out
            </button>
          </div>
        </header>

        {flashMessage ? (
          <div
            className={
              flashMessage.type === 'error'
                ? 'feedback feedback-error'
                : 'feedback feedback-success'
            }
          >
            {flashMessage.text}
          </div>
        ) : null}

        <AdminDomainList
          config={config}
          domains={adminDomains}
          isBusy={isDomainsLoading}
        />
      </main>
    );
  }

  return (
    <main className="app-shell">
      <header className="surface-panel flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            Sharkord Domains
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Claim and manage your{' '}
            <span className="font-mono">*.{config.baseDomain}</span> domains.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="rounded-full border border-border bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
            {domainsUsageLabel}
          </div>
          {isAdminUser ? (
            <a className="secondary-button" href="/admin">
              Admin panel
            </a>
          ) : null}
          <button className="secondary-button" onClick={onLogout} type="button">
            Log out
          </button>
        </div>
      </header>

      {flashMessage ? (
        <div
          className={
            flashMessage.type === 'error'
              ? 'feedback feedback-error'
              : 'feedback feedback-success'
          }
        >
          {flashMessage.text}
        </div>
      ) : null}

      <>
        <DomainClaimForm
          baseDomain={config.baseDomain}
          isBusy={isDomainsLoading || isClaimingDomain}
          maxPerUser={config.maxPerUser}
          onClaim={onClaimDomain}
          remainingClaims={remainingClaims}
        />
        <DomainList
          config={config}
          domains={domains}
          isBusy={isDomainsLoading}
          onDelete={onDeleteDomain}
          onUpdateSettings={onUpdateDomainSettings}
          pendingDeleteIds={pendingDeleteIds}
          pendingUpdateIds={pendingUpdateIds}
        />
      </>
    </main>
  );
};

export const App = memo(AppComponent);

export default App;
